import { TELEMETRY_LIMITS } from './telemetry.types';

const SESSION_KEY = 'dex.telemetry.session';
const ANON_KEY    = 'dex.telemetry.anon';

export interface TelemetrySessionState {
    id: string;
    startedAt: number;
    lastActivity: number;
    seq: number;
}

export interface SessionTouch {
    state: TelemetrySessionState;
    /** La sesión acaba de abrirse con esta acción. */
    started: boolean;
    /** La sesión anterior caducó por inactividad: hay que cerrarla con su propio id. */
    expired?: TelemetrySessionState;
}

/**
 * La sesión de navegación de esta pestaña. Vive en `sessionStorage`, que es por pestaña y sobrevive
 * a una recarga: recargar continúa la sesión, abrir otra pestaña empieza otra. Se relee en cada
 * acción porque el logout y la entrada de soporte como otro usuario vacían el almacenamiento sin
 * recargar, y eso tiene que abrir una sesión nueva. Sin almacenamiento disponible (modo privado
 * estricto) funciona en memoria.
 */
export class TelemetrySessionStore {
    private memory: TelemetrySessionState | null = null;

    constructor (
        private storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null,
        private newId: () => string,
    ) {}

    touch (now: number): SessionTouch {
        let state = this.read();
        let expired: TelemetrySessionState | undefined;

        if (state && now - state.lastActivity > TELEMETRY_LIMITS.sessionIdleMs) {
            expired = state;
            state = null;
        }

        const started = !state;
        if (!state) state = { id: this.newId(), startedAt: now, lastActivity: now, seq: 0 };

        return { state, started, expired };
    }

    /**
     * Reserva el siguiente número de orden y deja constancia de la actividad. Actualiza el mismo
     * objeto que recibe: dos eventos seguidos con el mismo estado no pueden repetir número.
     * `lastActivity` nunca retrocede (el evento de inactividad se fecha en el pasado).
     */
    next (state: TelemetrySessionState, now: number): number {
        const seq = state.seq;
        state.seq = seq + 1;
        state.lastActivity = Math.max(state.lastActivity, now);
        this.write(state);
        return seq;
    }

    /**
     * La última sesión conocida SIN releer el almacenamiento: al cerrar sesión otro servicio puede
     * haberlo vaciado ya, y aun así hay que cerrar la sesión que estaba abierta.
     */
    lastKnown (): TelemetrySessionState | null {
        return this.memory;
    }

    /** Olvida la sesión: la próxima acción abre otra. Es lo que hace el logout. */
    forget (): void {
        this.memory = null;
        try { this.storage?.removeItem(SESSION_KEY); } catch { /* nada que olvidar */ }
    }

    private read (): TelemetrySessionState | null {
        try {
            const raw = this.storage?.getItem(SESSION_KEY);
            if (raw) return this.memory = JSON.parse(raw);
            // Almacenamiento vacío con memoria llena: lo vaciaron (logout). Con almacenamiento
            // ausente o roto, la memoria es la única fuente.
            return this.storage ? null : this.memory;
        } catch {
            return this.memory;
        }
    }

    private write (state: TelemetrySessionState): void {
        this.memory = state;
        try { this.storage?.setItem(SESSION_KEY, JSON.stringify(state)); } catch { /* cuota o modo privado: queda en memoria */ }
    }
}

/** El identificador del navegador, anterior al login. Une el embudo de registro con la cuenta que sale de él. */
export function readAnonId (storage: Pick<Storage, 'getItem' | 'setItem'> | null, newId: () => string): string {
    try {
        const existing = storage?.getItem(ANON_KEY);
        if (existing) return existing;
        const created = newId();
        storage?.setItem(ANON_KEY, created);
        return created;
    } catch {
        return newId();
    }
}

export function newTelemetryId (): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
}
